<script setup lang="ts">
import CampaignEmailReputationGauge from './CampaignEmailReputationGauge.vue'

import type { ReputationState } from './CampaignEmailReputationGauge.vue'

export type FirstSendVariant = 'first' | 'second'

const props = defineProps<{ isOpen: boolean; variant: FirstSendVariant }>()

const emit = defineEmits<{
  (e: 'confirm'): void
  (e: 'close'): void
}>()

const { t } = useI18n()

const stepsByVariant: Record<FirstSendVariant, readonly string[]> = {
  first: ['start', 'measure', 'grow'],
  second: ['results', 'widen']
}

const steps = computed(() => stepsByVariant[props.variant])
const gaugeInitialState = computed<ReputationState>(() => (props.variant === 'second' ? 'healthy' : 'pending'))

const baseKey = computed(() => `pages.campaignEmail.firstSend.${props.variant}`)
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    size="lg"
    content-class="gap-5 p-6 duration-400 data-[state=open]:zoom-in-100! data-[state=open]:slide-in-from-bottom-20 sm:max-w-[460px]"
    overlay-class="duration-300"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-5">
      <CampaignEmailReputationGauge :initial-state="gaugeInitialState" />

      <div class="text-center">
        <h2 class="text-lg font-medium text-foreground">{{ t(`${baseKey}.title`) }}</h2>
        <p class="mt-2 text-sm leading-relaxed text-foreground-secondary">{{ t(`${baseKey}.intro`) }}</p>
      </div>

      <ol class="flex flex-col gap-2">
        <li v-for="(step, index) in steps" :key="step" class="flex gap-3.5 rounded-xl bg-app-bg px-3.5 py-2.5">
          <span
            class="mt-px flex size-5.5 shrink-0 items-center justify-center rounded-full border border-input bg-card text-xs text-foreground-secondary"
          >
            {{ index + 1 }}
          </span>
          <span>
            <span class="block text-sm font-medium text-foreground">{{ t(`${baseKey}.steps.${step}.title`) }}</span>
            <span class="mt-1 block text-sm leading-relaxed text-foreground-secondary">
              {{ t(`${baseKey}.steps.${step}.text`) }}
            </span>
          </span>
        </li>
      </ol>
    </div>

    <template #linkBtn>
      <Button variant="default-primary" class="w-full" @click="emit('confirm')">
        {{ t('pages.campaignEmail.firstSend.cta') }}
      </Button>
    </template>
  </BaseModal>
</template>
